import { useCallback, useEffect, useRef, useState, type Dispatch, type RefObject, type SetStateAction } from "react";
import type { TaskFocusModeController } from "./task-focus-mode";

type FocusMode = "off" | "fullscreen" | "fallback";
type FocusModeSetter = Dispatch<SetStateAction<FocusMode>>;

function syncFullscreen(shell: HTMLDivElement, setFocusMode: FocusModeSetter) {
  const onChange = () => {
    setFocusMode(document.fullscreenElement === shell ? "fullscreen" : (current) => current === "fullscreen" ? "off" : current);
  };
  document.addEventListener("fullscreenchange", onChange);
  return () => {
    document.removeEventListener("fullscreenchange", onChange);
    if (document.fullscreenElement === shell) void document.exitFullscreen().catch(() => undefined);
  };
}

function exitFallbackOnEscape(focusMode: FocusMode, setFocusMode: FocusModeSetter) {
  if (focusMode !== "fallback") return undefined;
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape" && !event.defaultPrevented) setFocusMode("off");
  };
  document.addEventListener("keydown", onKeyDown);
  return () => document.removeEventListener("keydown", onKeyDown);
}

function enterFocusMode(shell: HTMLDivElement, setFocusMode: FocusModeSetter, setIsChanging: Dispatch<SetStateAction<boolean>>) {
  if (typeof shell.requestFullscreen !== "function") {
    setFocusMode("fallback");
    return;
  }
  setIsChanging(true);
  void shell.requestFullscreen().then(
    () => setFocusMode("fullscreen"),
    () => setFocusMode("fallback"),
  ).finally(() => setIsChanging(false));
}

function leaveFocusMode(shell: HTMLDivElement, focusMode: FocusMode, setFocusMode: FocusModeSetter, setIsChanging: Dispatch<SetStateAction<boolean>>) {
  if (focusMode !== "fullscreen" || document.fullscreenElement !== shell) {
    setFocusMode("off");
    return;
  }
  setIsChanging(true);
  void document.exitFullscreen().then(
    () => setFocusMode("off"),
    () => setFocusMode(document.fullscreenElement === shell ? "fullscreen" : "fallback"),
  ).finally(() => setIsChanging(false));
}

export function useTaskFocusController(): {
  shellRef: RefObject<HTMLDivElement>;
  controller: TaskFocusModeController;
} {
  const shellRef = useRef<HTMLDivElement>(null);
  const [focusMode, setFocusMode] = useState<FocusMode>("off");
  const [isChanging, setIsChanging] = useState(false);
  const toggle = useCallback(() => {
    const shell = shellRef.current;
    if (!shell || isChanging) return;
    if (focusMode === "off") enterFocusMode(shell, setFocusMode, setIsChanging);
    else leaveFocusMode(shell, focusMode, setFocusMode, setIsChanging);
  }, [focusMode, isChanging]);

  useEffect(() => {
    const shell = shellRef.current;
    return shell ? syncFullscreen(shell, setFocusMode) : undefined;
  }, []);
  useEffect(() => exitFallbackOnEscape(focusMode, setFocusMode), [focusMode]);

  return { shellRef, controller: { isFocused: focusMode !== "off", isChanging, toggle } };
}
